import { VoEProvider } from 'src/graphql/types/carriers'

/**
 * How each verification platform is named to a recruiter. Mirrors
 * VOE_PROVIDER_LABELS in api/src/lib/voe/verificationPlatforms.ts.
 */
export const VOE_PROVIDER_LABELS: Record<VoEProvider, string> = {
  [VoEProvider.TENSTREET]: 'Tenstreet',
  [VoEProvider.DRIVER_REACH]: 'DriverReach',
  [VoEProvider.TRUCK_RIGHT]: 'TruckRight',
  [VoEProvider.CDL_SUITE]: 'CDL Suite',
  [VoEProvider.DOUBLE_NICKEL]: 'Double Nickel',
  [VoEProvider.DRIVER_FACTS]: 'DriverFacts',
  [VoEProvider.DRIVER_IQ]: 'DriverIQ',
  [VoEProvider.THE_WORK_NUMBER]: 'The Work Number',
  [VoEProvider.OTHER]: 'Other platform',
}

/** Falls back to the raw value for anything the label map does not know. */
export const voeProviderLabel = (
  provider: string | null | undefined
): string | null =>
  provider ? VOE_PROVIDER_LABELS[provider as VoEProvider] ?? provider : null

/** Options for a platform picker, in the order the enum lists them. */
export const VOE_PROVIDER_OPTIONS = Object.values(VoEProvider).map((value) => ({
  value,
  label: VOE_PROVIDER_LABELS[value],
}))

/**
 * Which platform a recruiter should be sent to: the preferred row, or the
 * first one when none is flagged.
 */
export const preferredPlatform = <T extends { preferred: boolean }>(
  platforms: ReadonlyArray<T> | null | undefined
): T | null =>
  platforms?.find((platform) => platform.preferred) ?? platforms?.[0] ?? null

/** Preferred platform first, then the rest in the order given. */
export const orderPlatforms = <T extends { preferred: boolean }>(
  platforms: ReadonlyArray<T> | null | undefined
): T[] => [
  ...(platforms ?? []).filter((platform) => platform.preferred),
  ...(platforms ?? []).filter((platform) => !platform.preferred),
]

/**
 * "Tenstreet (preferred), DriverFacts" — the same sentence the API writes
 * into the VoE's audit note (describeCarrierPlatforms), so the alert and the
 * note read alike.
 */
export const describePlatforms = (
  platforms: ReadonlyArray<{ provider: string; preferred: boolean }>
): string =>
  orderPlatforms(platforms)
    .map((platform) =>
      platform.preferred
        ? `${voeProviderLabel(platform.provider)} (preferred)`
        : voeProviderLabel(platform.provider)
    )
    .join(', ')

/**
 * The full name of the platform a VoE is routed through, or null when the
 * stored value is not one we know. VerificationOfEmployment.platform is a
 * free-text column with older writers, so an unknown value gets no label
 * rather than a made-up one.
 */
export const knownPlatformLabel = (
  platform: string | null | undefined
): string | null =>
  platform && platform in VOE_PROVIDER_LABELS
    ? voeProviderLabel(platform)
    : null
